"use client";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Zap,
  Home,
  User,
  Code,
  Briefcase,
  Mail,
  Github,
  Linkedin,
  FileText,
  Calendar,
  ArrowUpRight,
  Menu,
  X,
} from "lucide-react";
import ContactComponent from "./ContactForm";
import ArchitectureVisual from "./ArchitectureVisual";
import links from "@/utils";

const navItems = [
  { href: "#home", label: "Home", icon: Home },
  { href: "#about", label: "About", icon: User },
  { href: "#skills", label: "Skills", icon: Code },
  { href: "#projects", label: "Work", icon: Briefcase },
  { href: "#contact", label: "Contact", icon: Mail },
];

const skills = [
  {
    title: "Core",
    items: ["React.js", "Next.js", "TypeScript", "JavaScript"],
  },
  {
    title: "Interface",
    items: ["Tailwind CSS", "Bootstrap", "MUI", "Responsive UI"],
  },
  {
    title: "Real-time Communication",
    items: ["WebRTC", "Twilio", "Voice & Video"],
  },
  {
    title: "Delivery",
    items: ["REST APIs", "SEO", "Chrome extensions", "Admin systems"],
  },
];

const projects = [
  {
    index: "01",
    title: "HighRadius",
    copy: "Frontend work on a calling system: migrated SIP.js to Twilio and shipped new calling features for an Autonomous Finance platform used to lower DSO and improve working capital.",
    tags: ["React.js", "TypeScript", "MUI"],
    href: "https://www.highradius.com",
  },
  {
    index: "02",
    title: "Rillabox",
    copy: "Built an engaging gaming platform with Mystrix Box, Plinko, Crash Rocket, and more — a product where people play, spend, and win prizes.",
    tags: ["React.js", "Bootstrap"],
    href: "https://rillabox.com",
  },
  {
    index: "03",
    title: "Silq",
    copy: "Helped shape a next-generation legal platform for case management, billing, and client communication — focused on productivity for law firms.",
    tags: ["Next.js", "Tailwind CSS"],
    href: "https://www.silq.com.au",
  },
];

const Portfolio = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const close = () => setIsMobileMenuOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);

  return (
    <div className="site-bg relative min-h-screen overflow-x-hidden">
      <div className="site-grid pointer-events-none absolute inset-0" aria-hidden="true" />

      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed left-0 right-0 top-0 z-50"
      >
        <div className="mx-auto max-w-6xl px-4 pt-4 sm:px-6">
          <div className="flex items-center justify-between rounded-full border border-white/10 bg-[#07090c]/75 px-3 py-2 shadow-[0_10px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl">
            <motion.a
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 rounded-full px-2 py-1 outline-none focus-visible:ring-2 focus-visible:ring-[#3ee0c0]"
              href="#home"
              aria-label="Meet Majevadiya - Home"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#3ee0c0] text-sm font-semibold text-[#06110e]">
                MM
              </span>
              <span className="hidden pr-2 text-sm font-medium sm:block">
                Meet Majevadiya
              </span>
            </motion.a>
            <nav className="hidden items-center gap-1 md:flex">
              {navItems.map((item) => (
                <motion.a
                  key={item.href}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  className="rounded-full px-3 py-2 text-sm text-[#c9d3de] outline-none transition hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-[#3ee0c0]"
                  href={item.href}
                >
                  {item.label}
                </motion.a>
              ))}
            </nav>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="rounded-full p-2 text-[#f3efe6] md:hidden"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </motion.button>
          </div>
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mt-2 overflow-hidden rounded-3xl border border-white/10 bg-[#10151c]/95 p-3 backdrop-blur-xl md:hidden"
              >
                <nav className="flex flex-col">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <motion.a
                        key={item.href}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-2xl px-3 py-3 text-[#c9d3de] outline-none hover:bg-white/5 hover:text-white"
                        href={item.href}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </motion.a>
                    );
                  })}
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>

      <main className="relative">
        <section id="home" className="scroll-mt-28 px-4 pb-20 pt-32 sm:px-6 sm:pb-28 sm:pt-40">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <motion.div
              initial={{ x: -40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.7 }}
            >
              <p className="inline-flex items-center gap-2 rounded-full border border-[#3ee0c0]/30 bg-[#3ee0c0]/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.22em] text-[#3ee0c0]">
                5 years in development
              </p>
              <h1 className="font-display mt-6 text-5xl leading-[1.05] sm:text-6xl lg:text-7xl">
                Meet Majevadiya
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#c9d3de]">
                Frontend engineer shipping React and Next.js products — admin
                systems, SEO sites, gaming platforms, and calling experiences
                that stay fast, clear, and usable.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <motion.a
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  href="#projects"
                  className="inline-flex items-center gap-2 rounded-full bg-[#3ee0c0] px-5 py-3 text-sm font-medium text-[#06110e]"
                >
                  View work
                  <ArrowUpRight className="h-4 w-4" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  href={links.resume}
                  target="_blank"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm text-[#f3efe6] hover:bg-white/5"
                >
                  <FileText className="h-4 w-4" />
                  Resume
                </motion.a>
              </div>
              <dl className="mt-12 grid max-w-lg grid-cols-3 gap-4 border-t border-white/10 pt-8">
                {[
                  { label: "Years", value: "05" },
                  { label: "Focus", value: "UI" },
                  { label: "Stack", value: "React" },
                ].map((stat) => (
                  <div key={stat.label}>
                    <dt className="text-xs uppercase tracking-[0.2em] text-[#9aa6b5]">
                      {stat.label}
                    </dt>
                    <dd className="font-display mt-1 text-3xl">{stat.value}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>

            <motion.div
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative mx-auto w-full max-w-md"
            >
              <ArchitectureVisual />
            </motion.div>
          </div>
        </section>

        <section id="about" className="scroll-mt-28 px-4 py-20 sm:px-6 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#3ee0c0]">
              About
            </p>
            <motion.h2
              initial={{ y: 24, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="font-display mt-3 max-w-3xl text-4xl sm:text-5xl"
            >
              Five years of turning product ideas into interfaces people can
              trust.
            </motion.h2>
            <motion.p
              initial={{ y: 24, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="mt-6 max-w-3xl text-lg leading-relaxed text-[#c9d3de]"
            >
              With 5 years of hands-on experience, I have built admin panels,
              SEO-optimized websites, gaming platforms, Chrome extensions, and
              custom management systems. The through-line is the same: clean
              interfaces, maintainable code, and work that maps to business
              goals.
            </motion.p>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-7"
              >
                <h3 className="text-lg font-semibold">What I do</h3>
                <p className="mt-3 leading-relaxed text-[#9aa6b5]">
                  Frontend development, backend API integration, and third-party
                  services. I write maintainable code and follow practices that
                  keep products shipping.
                </p>
              </motion.div>
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-7"
              >
                <h3 className="text-lg font-semibold">What I&apos;m looking for</h3>
                <p className="mt-3 leading-relaxed text-[#9aa6b5]">
                  Opportunities to build impactful products, collaborate across
                  teams, and contribute to web solutions that feel sharp in the
                  browser and solid in the codebase.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <section id="achievements" className="px-4 py-10 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="flex flex-col gap-5 rounded-3xl border border-[#e8c089]/20 bg-gradient-to-r from-[#e8c089]/10 to-transparent p-7 sm:flex-row sm:items-center"
            >
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#e8c089]/15 text-[#e8c089]">
                <Zap className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Employee of the Month</h3>
                <p className="mt-1 text-[#9aa6b5]">
                  Recognized for exceptional performance and contributions —
                  August 2022.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section id="skills" className="scroll-mt-28 px-4 py-20 sm:px-6 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#3ee0c0]">
              Skills
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl">
              Tools I reach for daily.
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {skills.map((group, index) => (
                <motion.div
                  key={group.title}
                  initial={{ y: 24, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="rounded-3xl border border-white/10 bg-white/[0.03] p-7"
                >
                  <h3 className="text-sm uppercase tracking-[0.2em] text-[#9aa6b5]">
                    {group.title}
                  </h3>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className="rounded-full border border-white/10 bg-[#07090c] px-3 py-1.5 text-sm"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="projects" className="scroll-mt-28 px-4 py-20 sm:px-6 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#3ee0c0]">
              Selected work
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl">
              Products in production.
            </h2>
            <div className="mt-10 space-y-4">
              {projects.map((project, index) => (
                <motion.article
                  key={project.title}
                  initial={{ y: 28, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="group rounded-3xl border border-white/10 bg-white/[0.03] p-6 transition hover:border-[#3ee0c0]/30 hover:bg-white/[0.05] sm:p-8"
                >
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-3xl">
                      <p className="font-display text-3xl text-[#3ee0c0]/70">
                        {project.index}
                      </p>
                      <h3 className="mt-2 text-2xl font-semibold">{project.title}</h3>
                      <p className="mt-3 leading-relaxed text-[#9aa6b5]">
                        {project.copy}
                      </p>
                      <ul className="mt-5 flex flex-wrap gap-2">
                        {project.tags.map((tag) => (
                          <li
                            key={tag}
                            className="rounded-full border border-[#3ee0c0]/20 bg-[#3ee0c0]/10 px-3 py-1 text-xs text-[#3ee0c0]"
                          >
                            {tag}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <a
                      href={project.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 px-4 py-2 text-sm transition group-hover:border-[#3ee0c0]/40 group-hover:text-[#3ee0c0]"
                    >
                      Live site
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section id="education" className="px-4 py-20 sm:px-6 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#3ee0c0]">
              Education
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl">
              How I started.
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-7"
              >
                <h3 className="text-xl font-semibold">Bachelor of Engineering</h3>
                <p className="mt-2 text-[#c9d3de]">
                  Government Engineering College, Patan
                </p>
                <p className="mt-4 flex items-center gap-2 text-sm text-[#9aa6b5]">
                  <Calendar className="h-4 w-4" />
                  08/2017 — 05/2020
                </p>
              </motion.div>
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-7"
              >
                <h3 className="text-xl font-semibold">Diploma Engineering</h3>
                <p className="mt-2 text-[#c9d3de]">
                  Government Polytechnic Gandhinagar
                </p>
                <p className="mt-4 flex items-center gap-2 text-sm text-[#9aa6b5]">
                  <Calendar className="h-4 w-4" />
                  08/2014 — 05/2017
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <ContactComponent />
      </main>

      <footer className="border-t border-white/10 px-4 py-12 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <p className="font-display text-2xl">Meet Majevadiya</p>
            <p className="mt-1 text-sm text-[#9aa6b5]">
              Frontend engineer · 5 years
            </p>
          </div>
          <div className="flex gap-4">
            {/* <a
              href={links.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#c9d3de] hover:border-white/25 hover:text-white"
            >
              <Github className="h-4 w-4" />
              GitHub
            </a> */}
            <a
              href={links.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#c9d3de] hover:border-white/25 hover:text-white"
            >
              <Linkedin className="h-4 w-4" />
              LinkedIn
            </a>
          </div>
        </div>
        <p className="mx-auto mt-10 max-w-6xl text-center text-sm text-[#6f7b8a]">
          © 2026 Meet Majevadiya. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Portfolio;
